import React, { useState, useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Building2, Eye, EyeOff, Loader2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/context/AuthContext';
import { LoginCredentials } from '@/types/auth';
import { showError } from '@/utils/alerts';
import { companyService } from '@/services/companyService';

const schema = yup.object({
  email: yup
    .string()
    .email('Ingrese un email válido')
    .required('Email es requerido'),
  password: yup
    .string()
    .min(1, 'Contraseña es requerida')
    .required('Contraseña es requerida'),
});

export const Login: React.FC = () => {
  const { login, isAuthenticated } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginCredentials>({
    resolver: yupResolver(schema),
  });

  // Get company settings for branding
  const { data: companySettings } = useQuery({
    queryKey: ['company-settings'],
    queryFn: companyService.getCompanySettings,
    retry: false,
    refetchOnWindowFocus: false
  });

  // Define theme colors (same as ThemeContext)
  const themes = {
    blue: { 50: '#eff6ff', 100: '#dbeafe', 200: '#bfdbfe', 300: '#93c5fd', 400: '#60a5fa', 500: '#3b82f6', 600: '#2563eb', 700: '#1d4ed8', 800: '#1e40af', 900: '#1e3a8a', 950: '#172554' },
    green: { 50: '#f0fdf4', 100: '#dcfce7', 200: '#bbf7d0', 300: '#86efac', 400: '#4ade80', 500: '#22c55e', 600: '#16a34a', 700: '#15803d', 800: '#166534', 900: '#14532d', 950: '#052e16' },
    purple: { 50: '#faf5ff', 100: '#f3e8ff', 200: '#e9d5ff', 300: '#d8b4fe', 400: '#c084fc', 500: '#a855f7', 600: '#9333ea', 700: '#7c3aed', 800: '#6b21a8', 900: '#581c87', 950: '#3b0764' },
    red: { 50: '#fef2f2', 100: '#fee2e2', 200: '#fecaca', 300: '#fca5a5', 400: '#f87171', 500: '#ef4444', 600: '#dc2626', 700: '#b91c1c', 800: '#991b1b', 900: '#7f1d1d', 950: '#450a0a' },
    orange: { 50: '#fff7ed', 100: '#ffedd5', 200: '#fed7aa', 300: '#fdba74', 400: '#fb923c', 500: '#f97316', 600: '#ea580c', 700: '#c2410c', 800: '#9a3412', 900: '#7c2d12', 950: '#431407' },
  };

  // Apply theme when company settings load
  useEffect(() => {
    if (companySettings?.theme) {
      const themeColors = themes[companySettings.theme as keyof typeof themes];
      if (themeColors) {
        const existingStyle = document.getElementById('login-theme-styles');
        if (existingStyle) {
          existingStyle.remove();
        }

        const style = document.createElement('style');
        style.id = 'login-theme-styles';
        style.textContent = `
          :root {
            --color-primary-50: ${themeColors[50]};
            --color-primary-100: ${themeColors[100]};
            --color-primary-200: ${themeColors[200]};
            --color-primary-300: ${themeColors[300]};
            --color-primary-400: ${themeColors[400]};
            --color-primary-500: ${themeColors[500]};
            --color-primary-600: ${themeColors[600]};
            --color-primary-700: ${themeColors[700]};
            --color-primary-800: ${themeColors[800]};
            --color-primary-900: ${themeColors[900]};
            --color-primary-950: ${themeColors[950]};
          }
        `;
        document.head.appendChild(style);
      }
    }
  }, [companySettings?.theme]);

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const onSubmit = async (data: LoginCredentials) => {
    try {
      setIsLoading(true);
      await login(data);
    } catch (error: any) {
      showError(error.response?.data?.error?.message || 'Error al iniciar sesión');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Login Form */}
      <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-20 xl:px-24">
        <div className="mx-auto w-full max-w-sm lg:w-96">
          <div>
            <div className="flex items-center justify-center mb-8">
              {companySettings?.logo ? (
                <img
                  src={companyService.getFileUrl(companySettings.logo)}
                  alt={`${companySettings.name} logo`}
                  className={`h-16 w-16 object-cover ${companySettings.logo_shape === 'circle' ? 'rounded-full' : 'rounded'}`}
                />
              ) : (
                <Building2 className="h-12 w-12 text-primary-600" />
              )}
            </div>
            <h2 className="text-center text-3xl font-bold text-gray-900">
              Bienvenido a {companySettings?.name || 'tu Sistema Empresarial'}
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              Inicia sesión en tu cuenta
            </p>
          </div>

          <div className="mt-8">
            <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
              <div>
                <label htmlFor="email" className="form-label">
                  Email
                </label>
                <input
                  {...register('email')}
                  type="email"
                  autoComplete="email"
                  className="form-input"
                  placeholder="tu@email.com"
                />
                {errors.email && (
                  <p className="form-error">{errors.email.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="password" className="form-label">
                  Contraseña
                </label>
                <div className="relative">
                  <input
                    {...register('password')}
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    className="form-input pr-10"
                    placeholder="Tu contraseña"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-gray-400" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-400" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="form-error">{errors.password.message}</p>
                )}
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
                    Recordarme
                  </label>
                </div>

                <div className="text-sm">
                  <a
                    href="#"
                    className="font-medium text-primary-600 hover:text-primary-500"
                  >
                    ¿Olvidaste tu contraseña?
                  </a>
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="btn-primary w-full justify-center"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="animate-spin -ml-1 mr-3 h-4 w-4" />
                      Iniciando sesión...
                    </>
                  ) : (
                    'Iniciar Sesión'
                  )}
                </button>
              </div>

              <div className="text-center">
                <span className="text-sm text-gray-600">
                  ¿No tienes una cuenta?{' '}
                  <Link
                    to="/register"
                    className="font-medium text-primary-600 hover:text-primary-500"
                  >
                    Regístrate aquí
                  </Link>
                </span>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Right side - Image/Brand */}
      <div className="hidden lg:block relative w-0 flex-1">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-600 to-primary-800 flex items-center justify-center">
          <div className="text-center text-white px-8">
            {companySettings?.logo ? (
              <img
                src={companyService.getFileUrl(companySettings.logo)}
                alt={`${companySettings.name} logo`}
                className={`h-24 w-24 mx-auto mb-8 opacity-90 object-cover ${companySettings.logo_shape === 'circle' ? 'rounded-full' : 'rounded'}`}
              />
            ) : (
              <Building2 className="h-24 w-24 mx-auto mb-8 opacity-80" />
            )}
            <h3 className="text-3xl font-bold mb-4">
              {companySettings?.name || 'Sistema Empresarial Integral'}
            </h3>
            <p className="text-xl opacity-90 max-w-md">
              Administra clientes, productos, cotizaciones, ventas y citas desde una sola plataforma.
            </p>
            <div className="mt-8 space-y-2 text-primary-100">
              <p>✓ Control de transacciones completo</p>
              <p>✓ Gestión de citas con embajadas</p>
              <p>✓ Impresión POS y PDF</p>
              <p>✓ Reportes y auditoría</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};